import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";

const NavLink = ({ href, children, testid }) => (
  <a
    href={href}
    data-testid={testid}
    className="text-sm font-medium tracking-wide text-[#0B0A4A] hover:text-[#3E1A7A] transition-colors duration-200"
  >
    {children}
  </a>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F4ECFF]/80 backdrop-blur-xl border-b border-[#E0D0F5]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 sm:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-center gap-3"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_feminineways-notify/artifacts/vvjjpevv_FeminineWays%20logo.png"
            alt="Feminine Ways"
            className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl shadow-md"
          />
          <div className="flex flex-col leading-tight">
            <span
              className={`font-serif text-xl sm:text-2xl tracking-tight transition-colors duration-500 ${
                scrolled ? "text-[#0B0A4A]" : "text-[#F4ECFF]"
              }`}
            >
              Feminine<span className={scrolled ? "text-[#3E1A7A]" : "text-[#B87CFF]"}>Ways</span>
            </span>
            <span
              data-testid="nav-slogan"
              className={`hidden sm:block text-[10px] italic font-serif tracking-wide mt-0.5 transition-colors duration-500 ${
                scrolled ? "text-[#3E1A7A]/80" : "text-[#B87CFF]"
              }`}
            >
              Every woman is as rare as a shooting star <span className="not-italic">🌠</span>
            </span>
          </div>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          <a
            href="#features"
            data-testid="nav-features"
            className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
              scrolled ? "text-[#0B0A4A] hover:text-[#3E1A7A]" : "text-[#F4ECFF] hover:text-[#B87CFF]"
            }`}
          >
            Features
          </a>
          <a
            href="#pricing"
            data-testid="nav-pricing"
            className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
              scrolled ? "text-[#0B0A4A] hover:text-[#3E1A7A]" : "text-[#F4ECFF] hover:text-[#B87CFF]"
            }`}
          >
            Offer
          </a>
          <a
            href="#founder"
            data-testid="nav-founder"
            className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
              scrolled ? "text-[#0B0A4A] hover:text-[#3E1A7A]" : "text-[#F4ECFF] hover:text-[#B87CFF]"
            }`}
          >
            Founder
          </a>
          <a
            href="#faq"
            data-testid="nav-faq"
            className={`text-sm font-medium tracking-wide transition-colors duration-300 ${
              scrolled ? "text-[#0B0A4A] hover:text-[#3E1A7A]" : "text-[#F4ECFF] hover:text-[#B87CFF]"
            }`}
          >
            FAQ
          </a>
        </nav>

        <a
          href="#waitlist"
          data-testid="nav-cta-btn"
          className={`hidden md:inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-medium tracking-wide transition-all duration-300 ${
            scrolled
              ? "bg-[#0B0A4A] hover:bg-[#3E1A7A] text-[#F4ECFF]"
              : "bg-[#B87CFF] hover:bg-[#F4ECFF] text-[#0B0A4A]"
          }`}
        >
          Join Waitlist
        </a>

        <button
          className={`md:hidden p-2 -mr-2 transition-colors ${scrolled ? "text-[#0B0A4A]" : "text-[#F4ECFF]"}`}
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#F4ECFF]/95 backdrop-blur-xl border-t border-[#E0D0F5] px-6 py-6 flex flex-col gap-5">
          <a onClick={() => setOpen(false)} href="#features" className="text-base text-[#0B0A4A]" data-testid="nav-mobile-features">
            Features
          </a>
          <a onClick={() => setOpen(false)} href="#pricing" className="text-base text-[#0B0A4A]" data-testid="nav-mobile-pricing">
            Offer
          </a>
          <a onClick={() => setOpen(false)} href="#founder" className="text-base text-[#0B0A4A]" data-testid="nav-mobile-founder">
            Founder
          </a>
          <a onClick={() => setOpen(false)} href="#faq" className="text-base text-[#0B0A4A]" data-testid="nav-mobile-faq">
            FAQ
          </a>
          <a
            onClick={() => setOpen(false)}
            href="#waitlist"
            className="mt-2 inline-flex items-center justify-center rounded-full bg-[#3E1A7A] text-white px-5 py-3 text-sm font-medium tracking-wide"
            data-testid="nav-mobile-cta"
          >
            Join Waitlist
          </a>
        </div>
      )}
    </header>
  );
};

export default Nav;
